import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

const GOLD = '#d4af37';

interface PremisesItem {
  id: string;
  name: string;
  description: string;
  imageUrl: string;
  category: string;
}

interface PremisesContent {
  title: string;
  description: string;
  category: string;
  premises: PremisesItem[];
}

const PremisesGalleryManager = () => {
  const [content, setContent] = useState<PremisesContent>({
    title: 'Temple Premises',
    description: 'Gallery showcasing temple premises and architecture',
    category: 'premises',
    premises: [
      { id: '1', name: 'Main Sanctum', description: 'The main prayer hall', imageUrl: 'https://via.placeholder.com/400x300?text=Sanctum', category: 'sanctum' },
      { id: '2', name: 'Temple Courtyard', description: 'Central courtyard of the temple', imageUrl: 'https://via.placeholder.com/400x300?text=Courtyard', category: 'courtyard' },
      { id: '3', name: 'Gopuram', description: 'Temple tower structure', imageUrl: 'https://via.placeholder.com/400x300?text=Gopuram', category: 'gopuram' },
    ]
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  const loadContent = useCallback(() => {
    try {
      return onSnapshot(doc(db, 'gallery_content', 'premises'), (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data() as Partial<PremisesContent>;
          setContent((prev) => {
            const nextPremises = (data.premises ?? prev.premises).map((item: any) => ({
              ...item,
              category: item.category || 'general'
            }));
            return { ...prev, ...data, premises: nextPremises };
          });
        }
        setLoading(false);
      });
    } catch (error) {
      console.error('Error loading content:', error);
      setLoading(false);
      showSnackbar('Failed to load content', 'error');
      return () => {};
    }
  }, []);

  useEffect(() => {
    const unsubscribe = loadContent();
    return () => unsubscribe();
  }, [loadContent]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'gallery_content', 'premises'), content);
      showSnackbar('Premises gallery saved successfully', 'success');
    } catch (error) {
      console.error('Error saving:', error);
      showSnackbar('Failed to save content', 'error');
    } finally {
      setSaving(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddPremises = () => {
    const newPremises: PremisesItem = {
      id: Date.now().toString(),
      name: 'New Location',
      description: '',
      imageUrl: '',
      category: 'general'
    };
    setContent({
      ...content,
      premises: [...content.premises, newPremises]
    });
  };

  const handlePremisesChange = (id: string, field: keyof PremisesItem, value: string) => {
    setContent({
      ...content,
      premises: content.premises.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    });
  };

  const handleDeletePremises = (id: string) => {
    setContent({
      ...content,
      premises: content.premises.filter(item => item.id !== id)
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
            Premises Gallery Manager
          </Typography>
          <Typography variant="body2" sx={{ color: '#666' }}>
            Manage temple premises gallery content
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={loadContent}
            disabled={loading}
            variant="outlined"
            sx={{ borderColor: GOLD, color: GOLD, '&:hover': { bgcolor: `${GOLD}15` } }}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Box>
      </Box>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD, mb: 2 }}>
          🏛️ Gallery Settings
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gallery Title"
              value={content.title}
              onChange={(e) => setContent({ ...content, title: e.target.value })}
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Gallery Description"
              value={content.description}
              onChange={(e) => setContent({ ...content, description: e.target.value })}
              multiline
              rows={2}
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Category (Future Filter)"
              value={content.category}
              onChange={(e) => setContent({ ...content, category: e.target.value })}
              size="small"
              placeholder="premises"
              sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper elevation={0} sx={{ p: 3, mb: 3, border: `2px solid ${GOLD}`, borderRadius: 2, bgcolor: '#fafaf8' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: GOLD }}>
            🏗️ Premises ({content.premises.length})
          </Typography>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddPremises}
            variant="contained"
            sx={{ bgcolor: GOLD, color: '#000', fontWeight: 700 }}
          >
            Add Location
          </Button>
        </Box>

        <Grid container spacing={2}>
          {content.premises.map((premises) => (
            <Grid item xs={12} key={premises.id}>
              <Paper sx={{ p: 2, bgcolor: '#fff', border: `1px solid ${GOLD}` }}>
                <Grid container spacing={2} alignItems="flex-start">
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Location Name"
                      value={premises.name}
                      onChange={(e) => handlePremisesChange(premises.id, 'name', e.target.value)}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Image URL"
                      value={premises.imageUrl}
                      onChange={(e) => handlePremisesChange(premises.id, 'imageUrl', e.target.value)}
                      size="small"
                      placeholder="https://..."
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select
                        label="Category"
                        value={premises.category || 'general'}
                        onChange={(e) => handlePremisesChange(premises.id, 'category', e.target.value)}
                      >
                        <MenuItem value="general">General</MenuItem>
                        <MenuItem value="sanctum">Sanctum</MenuItem>
                        <MenuItem value="courtyard">Courtyard</MenuItem>
                        <MenuItem value="entrance">Entrance</MenuItem>
                        <MenuItem value="gopuram">Gopuram</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <IconButton
                      onClick={() => handleDeletePremises(premises.id)}
                      color="error"
                      sx={{ width: '100%' }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Description"
                      value={premises.description}
                      onChange={(e) => handlePremisesChange(premises.id, 'description', e.target.value)}
                      multiline
                      rows={2}
                      size="small"
                      sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: GOLD }, '&.Mui-focused fieldset': { borderColor: GOLD } } }}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PremisesGalleryManager;
